import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { keyframes } from 'styled-components'
import { message } from 'antd'
import { EyeInvisibleOutlined, EyeTwoTone, LockOutlined, UserOutlined } from '@ant-design/icons'
import { apiError } from 'utils/ExpensiveFunction'
import API from 'utils/api'
import { useToken } from '../../../hooks'

const inputFocus = keyframes`
  0% {
    border-color: rgba(100, 149, 237, 0.3);
    box-shadow: 0 0 0 0 rgba(100, 149, 237, 0.2);
  }
  100% {
    border-color: rgba(100, 149, 237, 0.8);
    box-shadow: 0 0 0 4px rgba(100, 149, 237, 0.1);
  }
`

const buttonHover = keyframes`
  0% {
    transform: translateY(0);
    box-shadow: 0 4px 15px rgba(100, 149, 237, 0.3);
  }
  100% {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(100, 149, 237, 0.4);
  }
`

const FormContainer = styled.form`
  width: 100%;
`

const InputGroup = styled.div`
  margin-bottom: 20px;
  position: relative;

  @media (max-width: 480px) {
    margin-bottom: 18px;
  }
`

const InputLabel = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 10px;
  font-size: 1.4rem;
  font-weight: 500;
  letter-spacing: 0.5px;

  @media (max-width: 1200px) {
    font-size: 1.2rem;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 6px;
  }
`

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const StyledInput = styled.input`
  width: 100%;
  padding: 16px 18px 16px 50px;
  background: #ffffff !important;
  border: 1px solid rgba(52, 58, 64, 0.15) !important;
  border-radius: 12px;
  color: #212529 !important;
  font-size: 1.6rem;
  transition: all 0.3s ease;
  outline: none;
  font-family: 'Spoqa Han Sans Neo', 'Roboto', sans-serif;
  box-shadow: 0 2px 8px rgba(52, 58, 64, 0.05) !important;

  &::placeholder {
    color: rgba(52, 58, 64, 0.5) !important;
    font-size: 1.4rem;
  }

  &:focus {
    background: #ffffff !important;
    border-color: rgba(52, 58, 64, 0.3) !important;
    color: #212529 !important;
    box-shadow: 0 0 0 3px rgba(52, 58, 64, 0.1) !important;
  }

  &:hover {
    border-color: rgba(52, 58, 64, 0.2) !important;
  }

  &:-webkit-autofill {
    -webkit-text-fill-color: #212529 !important;
    -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
  }

  &:-webkit-autofill:focus {
    -webkit-text-fill-color: #212529 !important;
    -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
  }

  @media (max-width: 1200px) {
    padding: 14px 16px 14px 45px;
    font-size: 1.4rem;

    &::placeholder {
      font-size: 1.2rem;
    }
  }

  @media (max-width: 480px) {
    padding: 12px 14px 12px 40px;
    font-size: 1.2rem;

    &::placeholder {
      font-size: 1rem;
    }
  }
`

const InputIcon = styled.div`
  position: absolute;
  left: 18px;
  color: rgba(100, 149, 237, 0.7);
  font-size: 1.8rem;
  z-index: 1;

  @media (max-width: 1200px) {
    left: 15px;
    font-size: 1.6rem;
  }

  @media (max-width: 480px) {
    left: 12px;
    font-size: 1.4rem;
  }
`

const PasswordToggle = styled.button`
  position: absolute;
  right: 15px;
  background: none;
  border: none;
  color: rgba(100, 149, 237, 0.7);
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;

  &:hover {
    color: rgba(100, 149, 237, 1);
  }

  @media (max-width: 480px) {
    right: 12px;
    font-size: 0.9rem;
  }
`

const SubmitButton = styled.button`
  width: 100%;
  padding: 18px;
  background: linear-gradient(135deg, #6495ed 0%, #4169e1 50%, #1e90ff 100%);
  border: none;
  border-radius: 12px;
  color: #ffffff;
  font-size: 1.6rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 15px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    animation: ${buttonHover} 0.3s ease-out forwards;

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 1200px) {
    padding: 16px;
    font-size: 1.4rem;
  }

  @media (max-width: 480px) {
    padding: 14px;
    font-size: 1.2rem;
  }
`

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 0.85rem;
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 0.8rem;
    padding: 6px 10px;
  }
`

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

interface ModernLoginFormProps {
  setLoginSuccess: (value: string) => void;
}

const ModernLoginForm: React.FC<ModernLoginFormProps> = ({ setLoginSuccess }) => {
  const { t } = useTranslation()
  const { mutate } = useToken()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string>('')

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (error) setError('') // Clear error when user starts typing
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email || !formData.password) {
      setError('아이디와 비밀번호를 모두 입력해주세요.')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const res = await API.send('POST', '/auth/adsignin', null, {
        type: 'email',
        email: formData.email,
        iso_code: '',
        country_code: '',
        phone: '',
        password: formData.password,
        referral_code: '',
      })

      if (res.result.success) {
        message.success(t('loginSuccessMessage'))
        if (res?.result?.token) {
          mutate(res?.result?.token)
        } else {
          setLoginSuccess(res?.result?.verifyid)
        }
      } else {
        const errorResult = apiError(t, res.result.msgcode)
        setError(t(errorResult))
      }
    } catch (e) {
      console.log(e, 'ERROR LOGIN')
      setError('로그인 중 오류가 발생했습니다. 다시 시도해주세요.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit(e as any)
    }
  }

  return (
    <FormContainer onSubmit={handleSubmit}>
      <InputGroup>
        <InputLabel style={{
          display: 'block',
          color: 'rgba(52, 58, 64, 0.9)',
          marginBottom: '10px',
          fontSize: '1.4rem',
          fontWeight: 500,
          letterSpacing: '0.5px'
        }}>아이디</InputLabel>
        <InputWrapper>
          <InputIcon>
            <UserOutlined />
          </InputIcon>
          <StyledInput
            type="text"
            placeholder="developer"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '16px 18px 16px 50px',
              background: '#ffffff !important',
              border: '1px solid rgba(52, 58, 64, 0.15) !important',
              borderRadius: '12px',
              color: '#212529 !important',
              fontSize: '1.4rem',
              fontFamily: 'Spoqa Han Sans Neo, Roboto, sans-serif',
              boxShadow: '0 2px 8px rgba(52, 58, 64, 0.05) !important'
            }}
          />
        </InputWrapper>
      </InputGroup>

      <InputGroup>
        <InputLabel style={{
          display: 'block',
          color: 'rgba(52, 58, 64, 0.9)',
          marginBottom: '10px',
          fontSize: '1.4rem',
          fontWeight: 500,
          letterSpacing: '0.5px'
        }}>비밀번호</InputLabel>
        <InputWrapper>
          <InputIcon>
            <LockOutlined />
          </InputIcon>
          <StyledInput
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '16px 18px 16px 50px',
              background: '#ffffff !important',
              border: '1px solid rgba(52, 58, 64, 0.15) !important',
              borderRadius: '12px',
              color: '#212529 !important',
              fontSize: '1.4rem',
              fontFamily: 'Spoqa Han Sans Neo, Roboto, sans-serif',
              boxShadow: '0 2px 8px rgba(52, 58, 64, 0.05) !important'
            }}
          />
          <PasswordToggle
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
          </PasswordToggle>
        </InputWrapper>
      </InputGroup>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <SubmitButton
        type="submit"
        disabled={isLoading}
        style={{
          width: '100%',
          padding: '18px',
          background: 'linear-gradient(135deg, #495057 0%, #343a40 50%, #212529 100%)',
          border: 'none',
          borderRadius: '12px',
          color: '#ffffff',
          fontSize: '1.6rem',
          fontWeight: 600,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          cursor: 'pointer',
          marginTop: '15px'
        }}
      >
        {isLoading && <LoadingSpinner />}
        {isLoading ? '로그인 중...' : '로그인'}
      </SubmitButton>
    </FormContainer>
  )
}

export default React.memo(ModernLoginForm)